import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Tab,
    Tabs,
    TextField as MuiTextField,
    Typography,
} from '@mui/material';
import { formatDistance } from 'date-fns';
import { useState } from 'react';
import {
    RecordContextProvider,
    ReferenceManyField,
    ShowBase,
    useCreate,
    useDelete,
    useGetIdentity,
    useGetList,
    useListContext,
    useNotify,
    useRedirect,
    useRefresh,
    useShowContext,
    useUpdate,
} from 'react-admin';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Building2,
    Edit3,
    ExternalLink,
    Globe,
    Mail,
    MapPin,
    Phone,
    Search,
    UserPlus,
    X,
} from 'lucide-react';

import { ActivityLog } from '../activity/ActivityLog';
import { Avatar as ContactAvatar } from '../contacts/Avatar';
import { findQuoteLabel } from '../quotes/quote';
import { useConfigurationContext } from '../root/ConfigurationContext';
import { Account, Contact, Quote } from '../types';
import { AccountEditDialog } from './AccountEditDialog';

export const AccountShow = () => (
    <ShowBase>
        <AccountShowContent />
    </ShowBase>
);

const AccountShowContent = () => {
    const { record, isPending } = useShowContext<Account>();
    const [activeTab, setActiveTab] = useState(0);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteOne] = useDelete();
    const notify = useNotify();
    const redirect = useRedirect();

    if (isPending || !record) return null;

    const initials = (record.name || '')
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    return (
        <Box sx={{ mt: 2 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IconButton
                    component={RouterLink}
                    to="/accounts"
                    size="small"
                    sx={{ color: '#64748b' }}
                >
                    <ArrowLeft size={18} />
                </IconButton>
                <Avatar
                    sx={{
                        width: 44,
                        height: 44,
                        backgroundColor: '#10b981',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#0f172a',
                        }}
                    >
                        {record.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        {record.website && (
                            <Typography
                                component="a"
                                href={record.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#10b981',
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <Globe size={12} />
                                {record.website.replace(/^https?:\/\//, '')}
                            </Typography>
                        )}
                        {record.phone_number && (
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#64748b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Phone size={12} />
                                {record.phone_number}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Chip
                    label={`${record.nb_contacts ?? 0} contacts`}
                    size="small"
                    sx={{
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    }}
                />
                <Chip
                    label={`${record.nb_quotes ?? 0} quotes`}
                    size="small"
                    sx={{
                        backgroundColor: '#eff6ff',
                        color: '#1e40af',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    }}
                />
                <IconButton
                    size="small"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{
                        color: '#ef4444',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#fef2f2',
                        },
                    }}
                >
                    <X size={16} />
                </IconButton>
            </Box>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                sx={{
                    mb: 2,
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#10b981',
                    },
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#64748b',
                        '&.Mui-selected': {
                            color: '#10b981',
                        },
                    },
                }}
            >
                <Tab label="Overview" />
                <Tab
                    label={
                        record.nb_contacts
                            ? `Contacts (${record.nb_contacts})`
                            : 'Contacts'
                    }
                />
                <Tab
                    label={
                        record.nb_quotes
                            ? `Quotes (${record.nb_quotes})`
                            : 'Quotes'
                    }
                />
            </Tabs>

            {/* Tab Content */}
            {activeTab === 0 && (
                <OverviewTab
                    record={record}
                    onEdit={() => setEditDialogOpen(true)}
                />
            )}
            {activeTab === 1 && <ContactsTab record={record} />}
            {activeTab === 2 && <QuotesTab record={record} />}

            {/* Edit Dialog */}
            <AccountEditDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                account={record}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: '12px', maxWidth: 400 },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#0f172a' }}>
                    Delete Account
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569' }}>
                        Are you sure you want to delete{' '}
                        <strong>{record.name}</strong>?
                        {(record.nb_quotes ?? 0) > 0 && (
                            <>
                                {' '}
                                This account has{' '}
                                <strong>
                                    {record.nb_quotes} quote
                                    {record.nb_quotes === 1 ? '' : 's'}
                                </strong>{' '}
                                that will also be deleted.
                            </>
                        )}{' '}
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{
                            color: '#64748b',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            deleteOne(
                                'accounts',
                                {
                                    id: record.id,
                                    previousData: record,
                                },
                                {
                                    onSuccess: () => {
                                        notify('Account deleted', {
                                            type: 'info',
                                        });
                                        redirect('/accounts');
                                    },
                                    onError: () => {
                                        notify('Failed to delete account', {
                                            type: 'error',
                                        });
                                    },
                                }
                            );
                            setDeleteDialogOpen(false);
                        }}
                        sx={{
                            backgroundColor: '#ef4444',
                            '&:hover': {
                                backgroundColor: '#dc2626',
                            },
                            textTransform: 'none',
                            borderRadius: '8px',
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/* ─────────────── Overview Tab ─────────────── */

const OverviewTab = ({
    record,
    onEdit,
}: {
    record: Account;
    onEdit: () => void;
}) => (
    <Box
        display="flex"
        gap={2}
        sx={{
            flexDirection: { xs: 'column', md: 'row' },
        }}
    >
        {/* Account Info Card */}
        <Card
            sx={{
                flex: 1,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #e2e8f0',
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Building2 size={16} color="#10b981" />
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#0f172a',
                        }}
                    >
                        Account Details
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onEdit}
                    sx={{
                        color: '#64748b',
                        '&:hover': {
                            backgroundColor: '#f1f5f9',
                        },
                    }}
                >
                    <Edit3 size={15} />
                </IconButton>
            </Box>

            <Box sx={{ px: 2.5, py: 2 }}>
                <Stack spacing={2}>
                    <InfoRow label="Account Name" value={record.name} />
                    <InfoRow
                        label="Phone Number"
                        value={record.phone_number}
                        icon={<Phone size={14} />}
                    />
                    <InfoRow
                        label="Website"
                        value={
                            record.website ? (
                                <Typography
                                    component="a"
                                    href={record.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        fontSize: '0.8125rem',
                                        color: '#10b981',
                                        textDecoration: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {record.website.replace(/^https?:\/\//, '')}
                                    <ExternalLink size={12} />
                                </Typography>
                            ) : null
                        }
                        icon={<Globe size={14} />}
                    />
                    <Divider />
                    <InfoRow
                        label="Street Address"
                        value={record.address}
                        icon={<MapPin size={14} />}
                    />
                    <InfoRow label="City" value={record.city} />
                    <InfoRow label="State" value={record.stateAbbr} />
                    <InfoRow label="Zip Code" value={record.zipcode} />
                </Stack>
            </Box>
        </Card>

        {/* Recent Activity Card */}
        <Card
            sx={{
                flex: 1,
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 520,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #e2e8f0',
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#0f172a',
                    }}
                >
                    Recent Activity
                </Typography>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 1,
                }}
            >
                <ActivityLog
                    accountId={record.id}
                    context="account"
                    pageSize={10}
                />
            </Box>
        </Card>
    </Box>
);

const InfoRow = ({
    label,
    value,
    icon,
}: {
    label: string;
    value?: string | React.ReactNode | null;
    icon?: React.ReactNode;
}) => (
    <Box display="flex" alignItems="flex-start" gap={1.5}>
        {icon && (
            <Box
                sx={{
                    color: '#94a3b8',
                    mt: '2px',
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                }}
            >
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography
                    sx={{
                        fontSize: '0.8125rem',
                        color: value ? '#0f172a' : '#cbd5e1',
                        fontWeight: value ? 500 : 400,
                    }}
                >
                    {value || '—'}
                </Typography>
            ) : (
                value || (
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            color: '#cbd5e1',
                        }}
                    >
                        —
                    </Typography>
                )
            )}
        </Box>
    </Box>
);

/* ─────────────── Contacts Tab ─────────────── */

const ContactsTab = ({ record }: { record: Account }) => {
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    return (
        <>
            <Card
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#0f172a',
                        }}
                    >
                        Contacts
                    </Typography>
                    <Button
                        onClick={() => setAddDialogOpen(true)}
                        startIcon={<UserPlus size={14} />}
                        size="small"
                        sx={{
                            textTransform: 'none',
                            color: '#10b981',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#f0fdf4',
                            },
                        }}
                    >
                        Add Contact
                    </Button>
                </Box>
                <ReferenceManyField
                    reference="contacts"
                    target="account_id"
                    sort={{ field: 'last_name', order: 'ASC' }}
                >
                    <ContactsIterator />
                </ReferenceManyField>
            </Card>
            <AddContactDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                account={record}
            />
        </>
    );
};

/* ─────────────── Add Contact Dialog ─────────────── */

const AddContactDialog = ({
    open,
    onClose,
    account,
}: {
    open: boolean;
    onClose: () => void;
    account: Account;
}) => {
    const [mode, setMode] = useState<'search' | 'create'>('search');
    const [searchQuery, setSearchQuery] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [update] = useUpdate();
    const [create] = useCreate();
    const { identity } = useGetIdentity();
    const notify = useNotify();
    const refresh = useRefresh();

    const { data: searchResults } = useGetList<Contact>(
        'contacts',
        {
            filter: searchQuery ? { q: searchQuery } : { id: -1 },
            pagination: { page: 1, perPage: 10 },
            sort: { field: 'last_name', order: 'ASC' },
        },
        { enabled: mode === 'search' && searchQuery.length > 0 }
    );

    const handleClose = () => {
        onClose();
        setMode('search');
        setSearchQuery('');
        setFirstName('');
        setLastName('');
        setEmail('');
    };

    const handleLink = (contact: Contact) => {
        if (contact.account_id && contact.account_id !== account.id) {
            notify(
                'This contact is already linked to another account. Unlink them first.',
                { type: 'warning' }
            );
            return;
        }
        update(
            'contacts',
            {
                id: contact.id,
                data: { account_id: account.id },
                previousData: contact,
            },
            {
                onSuccess: () => {
                    notify('Contact linked to account', {
                        type: 'info',
                    });
                    refresh();
                    handleClose();
                },
                onError: () => {
                    notify('Failed to link contact', {
                        type: 'error',
                    });
                },
            }
        );
    };

    const handleCreate = () => {
        if (!firstName || !lastName) return;
        create(
            'contacts',
            {
                data: {
                    first_name: firstName,
                    last_name: lastName,
                    email_jsonb: email ? [{ email, type: 'Work' }] : [],
                    phone_jsonb: [],
                    account_id: account.id,
                    sales_id: identity?.id,
                    status: 'cold',
                    first_seen: new Date().toISOString(),
                    last_seen: new Date().toISOString(),
                    tags: [],
                },
            },
            {
                onSuccess: () => {
                    notify('Contact created and linked', {
                        type: 'info',
                    });
                    refresh();
                    handleClose();
                },
                onError: () => {
                    notify('Failed to create contact', {
                        type: 'error',
                    });
                },
            }
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    minWidth: 400,
                    maxHeight: '80vh',
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600, color: '#0f172a' }}>
                Add Contact to {account.name}
            </DialogTitle>
            <DialogContent>
                {/* Mode toggle */}
                <Box
                    display="flex"
                    gap={1}
                    mb={2}
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                        pb: 1,
                    }}
                >
                    <Button
                        size="small"
                        onClick={() => setMode('search')}
                        sx={{
                            textTransform: 'none',
                            fontWeight: mode === 'search' ? 600 : 400,
                            color: mode === 'search' ? '#10b981' : '#64748b',
                            borderBottom:
                                mode === 'search'
                                    ? '2px solid #10b981'
                                    : '2px solid transparent',
                            borderRadius: 0,
                        }}
                    >
                        Search Existing
                    </Button>
                    <Button
                        size="small"
                        onClick={() => setMode('create')}
                        sx={{
                            textTransform: 'none',
                            fontWeight: mode === 'create' ? 600 : 400,
                            color: mode === 'create' ? '#10b981' : '#64748b',
                            borderBottom:
                                mode === 'create'
                                    ? '2px solid #10b981'
                                    : '2px solid transparent',
                            borderRadius: 0,
                        }}
                    >
                        Create New
                    </Button>
                </Box>

                {mode === 'search' ? (
                    <>
                        <MuiTextField
                            fullWidth
                            size="small"
                            placeholder="Search contacts by name or email..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Search
                                        size={16}
                                        color="#94a3b8"
                                        style={{ marginRight: 8 }}
                                    />
                                ),
                            }}
                            sx={{ mb: 1 }}
                        />
                        {searchResults && searchResults.length > 0 ? (
                            <List dense sx={{ py: 0 }}>
                                {searchResults.map(contact => (
                                    <ListItem key={contact.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => handleLink(contact)}
                                            sx={{
                                                borderRadius: '6px',
                                                '&:hover': {
                                                    backgroundColor: '#f0fdf4',
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={`${contact.first_name} ${contact.last_name}`}
                                                secondary={
                                                    contact.email_jsonb?.[0]
                                                        ?.email || undefined
                                                }
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                }}
                                                secondaryTypographyProps={{
                                                    fontSize: '0.75rem',
                                                    color: '#64748b',
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : searchQuery ? (
                            <Typography
                                sx={{
                                    color: '#94a3b8',
                                    fontSize: '0.875rem',
                                    py: 2,
                                    textAlign: 'center',
                                }}
                            >
                                No contacts found
                            </Typography>
                        ) : null}
                    </>
                ) : (
                    <Stack spacing={2}>
                        <MuiTextField
                            label="First Name"
                            size="small"
                            fullWidth
                            required
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                        />
                        <MuiTextField
                            label="Last Name"
                            size="small"
                            fullWidth
                            required
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                        />
                        <MuiTextField
                            label="Email"
                            size="small"
                            fullWidth
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: '#64748b',
                        textTransform: 'none',
                    }}
                >
                    Cancel
                </Button>
                {mode === 'create' && (
                    <Button
                        variant="contained"
                        disabled={!firstName || !lastName}
                        onClick={handleCreate}
                        sx={{
                            backgroundColor: '#10b981',
                            '&:hover': {
                                backgroundColor: '#059669',
                            },
                            textTransform: 'none',
                            borderRadius: '8px',
                        }}
                    >
                        Create & Link
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

const ContactsIterator = () => {
    const location = useLocation();
    const { data: contacts, error, isPending } = useListContext<Contact>();

    if (isPending || error) return null;

    if (!contacts?.length) {
        return (
            <Box py={4} textAlign="center">
                <Typography
                    sx={{
                        color: '#94a3b8',
                        fontSize: '0.875rem',
                    }}
                >
                    No contacts yet
                </Typography>
            </Box>
        );
    }

    return (
        <List dense sx={{ py: 0 }}>
            {contacts.map(contact => (
                <RecordContextProvider key={contact.id} value={contact}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={RouterLink}
                            to={`/contacts/${contact.id}/show`}
                            state={{
                                from: location.pathname,
                            }}
                            sx={{
                                px: 2.5,
                                py: 1,
                                '&:hover': {
                                    backgroundColor: '#f8fafc',
                                },
                            }}
                        >
                            <ListItemAvatar>
                                <ContactAvatar record={contact} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={`${contact.first_name} ${contact.last_name}`}
                                secondary={contact.title || undefined}
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#0f172a',
                                }}
                                secondaryTypographyProps={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                }}
                            />
                            {contact.email_jsonb?.[0]?.email && (
                                <ListItemSecondaryAction>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                    >
                                        <Mail size={12} color="#94a3b8" />
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: '#64748b',
                                            }}
                                        >
                                            {contact.email_jsonb[0].email}
                                        </Typography>
                                    </Box>
                                </ListItemSecondaryAction>
                            )}
                        </ListItemButton>
                    </ListItem>
                </RecordContextProvider>
            ))}
        </List>
    );
};

/* ─────────────── Quotes Tab ─────────────── */

const QuotesTab = ({ record: _record }: { record: Account }) => (
    <Card
        sx={{
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 1.5,
                borderBottom: '1px solid #e2e8f0',
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#0f172a',
                }}
            >
                Quotes
            </Typography>
        </Box>
        <ReferenceManyField
            reference="quotes"
            target="account_id"
            sort={{ field: 'updated_at', order: 'DESC' }}
        >
            <QuotesIterator />
        </ReferenceManyField>
    </Card>
);

const QuotesIterator = () => {
    const { data: quotes, error, isPending } = useListContext<Quote>();
    const { quoteStages } = useConfigurationContext();

    if (isPending || error) return null;

    if (!quotes?.length) {
        return (
            <Box py={4} textAlign="center">
                <Typography
                    sx={{
                        color: '#94a3b8',
                        fontSize: '0.875rem',
                    }}
                >
                    No quotes yet
                </Typography>
            </Box>
        );
    }

    const now = Date.now();
    return (
        <List dense sx={{ py: 0 }}>
            {quotes.map(quote => (
                <ListItem disablePadding key={quote.id}>
                    <ListItemButton
                        component={RouterLink}
                        to={`/quotes/${quote.id}/page`}
                        sx={{
                            px: 2.5,
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#f8fafc',
                            },
                        }}
                    >
                        <ListItemText
                            primary={quote.name}
                            secondary={
                                <>
                                    {findQuoteLabel(quoteStages, quote.stage)}
                                    {quote.amount != null &&
                                        `, ${quote.amount.toLocaleString(
                                            'en-US',
                                            {
                                                notation: 'compact',
                                                style: 'currency',
                                                currency: 'USD',
                                                currencyDisplay: 'narrowSymbol',
                                                minimumSignificantDigits: 3,
                                            }
                                        )}`}
                                    {quote.category
                                        ? `, ${quote.category}`
                                        : ''}
                                </>
                            }
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#0f172a',
                            }}
                            secondaryTypographyProps={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                            }}
                        />
                        <ListItemSecondaryAction>
                            <Typography
                                sx={{
                                    fontSize: '0.6875rem',
                                    color: '#94a3b8',
                                }}
                            >
                                {formatDistance(quote.updated_at, now)} ago
                            </Typography>
                        </ListItemSecondaryAction>
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
};
